import React, { useState } from "react";
import { useSelector } from "react-redux";
import { getTodayString } from "../../../utils/helpers";

const ClockIcon = () => (
  <svg className="w-5 h-5 text-gray-800 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DAYS = [
  { key: "MON", label: "Monday" },
  { key: "TUE", label: "Tuesday" },
  { key: "WED", label: "Wednesday" },
  { key: "THU", label: "Thursday" },
  { key: "FRI", label: "Friday" },
  { key: "SAT", label: "Saturday" },
  { key: "SUN", label: "Sunday" },
];

const CalendarTimelineView = ({ appointments, selectedDate, onSelectTimeSlot }) => {
  const [viewMode, setViewMode] = useState("DAY"); // 'DAY' | 'SHIFT_TIMELINE'
  const doctorSchedule = useSelector((state) => state.appointment.doctorSchedule);

  const slotInterval = doctorSchedule?.slotDurationMinutes || 15;
  const weeklySchedule = doctorSchedule?.weeklySchedule || {};



  // Generate 15-min or 30-min time slots from 08:00 AM to 09:30 PM for Day View
  const timeSlots = [];
  let startHour = 8;
  let endHour = 21;
  for (let h = startHour; h <= endHour; h++) {
    const hourStr = h < 10 ? `0${h}` : `${h}`;
    timeSlots.push(`${hourStr}:00`);
    if (slotInterval <= 15) {
      timeSlots.push(`${hourStr}:15`);
      timeSlots.push(`${hourStr}:30`);
      timeSlots.push(`${hourStr}:45`);
    } else {
      timeSlots.push(`${hourStr}:30`);
    }
  }

  const normalizeDate = (d) => {
    if (!d) return "";
    const str = String(d).trim();
    if (str.includes("T")) return str.split("T")[0];
    if (str.includes(" ")) return str.split(" ")[0];
    return str;
  };

  const targetDate = normalizeDate(selectedDate || getTodayString());

  const normalizeTime = (t) => {
    if (!t) return "";
    let str = String(t).trim().toUpperCase();
    if (str.includes("AM") || str.includes("PM")) {
      const isPM = str.includes("PM");
      str = str.replace(/AM|PM/g, "").trim();
      const parts = str.split(":");
      let h = parseInt(parts[0], 10);
      const m = parts[1] || "00";
      if (isPM && h < 12) h += 12;
      if (!isPM && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${m.substring(0, 2)}`;
    }
    const parts = str.split(":");
    return `${String(parts[0]).padStart(2, "0")}:${String(parts[1] || "00").substring(0, 2)}`;
  };

  // Match appointments for the selected date
  let dayAppointments = appointments.filter((a) => {
    if (!a.appointmentDate) return true;
    return normalizeDate(a.appointmentDate) === targetDate;
  });

  if (dayAppointments.length === 0 && appointments.length > 0 && appointments.length <= 30) {
    dayAppointments = appointments;
  }

  // Find appointment in a time slot
  const getAppointmentAtSlot = (slotTime) => {
    const normSlot = normalizeTime(slotTime);
    return dayAppointments.find((a) => {
      if (!a.slotStartTime) return false;
      return normalizeTime(a.slotStartTime) === normSlot;
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "CHECKED_IN":
        return "bg-amber-100 text-amber-900 border-amber-300";
      case "IN_CONSULTATION":
        return "bg-purple-100 text-purple-900 border-purple-300 animate-pulse";
      case "COMPLETED":
        return "bg-emerald-100 text-emerald-900 border-emerald-300";
      case "CANCELLED":
        return "bg-red-100 text-red-800 border-red-200 line-through";
      case "NO_SHOW":
        return "bg-gray-200 text-gray-800 border-gray-300 line-through";
      default:
        return "bg-blue-100 text-blue-900 border-blue-300";
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-6 space-y-6">
      {/* View Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ClockIcon />
            <span>{viewMode === "DAY" ? "Daily Appointment Slots Grid" : "Weekly Doctor Shift Timeline"}</span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {viewMode === "DAY"
              ? "Click on any open time slot to book an appointment for " + selectedDate
              : "Overview of Doctor working shifts and OPD hours throughout the week"}
          </p>
        </div>

        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setViewMode("DAY")}
            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
              viewMode === "DAY"
                ? "bg-white text-gray-900 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Day View
          </button>
          <button
            type="button"
            onClick={() => setViewMode("SHIFT_TIMELINE")}
            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
              viewMode === "SHIFT_TIMELINE"
                ? "bg-white text-gray-900 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Shift Timeline
          </button>
        </div>
      </div>



      {/* Mode 1: DAY VIEW */}
      {viewMode === "DAY" && (
        <div className="border border-gray-100 rounded-2xl overflow-hidden divide-y divide-gray-100">
          {timeSlots.map((slot) => {
            const appointment = getAppointmentAtSlot(slot);
            return (
              <div
                key={slot}
                className="flex items-center hover:bg-gray-50/80 transition-all min-h-[50px] group"
              >
                {/* Time Slot Label */}
                <div className="w-24 p-3 font-mono text-xs font-bold text-gray-500 border-r border-gray-100 bg-gray-50/50 flex items-center justify-center shrink-0">
                  {slot}
                </div>

                {/* Appointment Content or Bookable Cell */}
                <div className="flex-1 p-2">
                  {appointment ? (
                    <div
                      className={`p-3 rounded-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 shadow-2xs ${getStatusBadgeColor(
                        appointment.status
                      )}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-black px-2 py-0.5 rounded bg-white/70 shadow-2xs">
                          Token #{appointment.tokenNumber}
                        </span>
                        <div>
                          <h4 className="font-bold text-xs">{appointment.patientName}</h4>
                          <p className="text-[11px] opacity-80">
                            📞 {appointment.patientPhone} | Fee: ₹{appointment.consultationFee} ({appointment.paymentStatus})
                          </p>
                          {appointment.cancelledReason && (
                            <p className="text-[10px] text-red-700 font-semibold mt-0.5" title={appointment.cancelledReason}>
                              Reason: {appointment.cancelledReason}
                            </p>
                          )}
                        </div>
                      </div>

                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-white/80">
                        {appointment.status}
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSelectTimeSlot(slot)}
                      className="w-full h-9 rounded-xl border border-dashed border-gray-200 hover:border-primary-400 hover:bg-primary-50/40 text-gray-400 hover:text-primary-700 text-xs font-bold flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                    >
                      <span>+ Book Slot at {slot}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mode 2: SHIFT TIMELINE */}
      {viewMode === "SHIFT_TIMELINE" && (
        <div className="space-y-4">
          {DAYS.map((day) => {
            const dayData = weeklySchedule[day.key] || { active: false };
            return (
              <div
                key={day.key}
                className={`p-4 rounded-2xl border transition-all ${
                  dayData.active ? "bg-white border-gray-200 shadow-2xs" : "bg-gray-50/50 border-gray-100 opacity-60"
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="w-24 text-sm font-bold text-gray-900">{day.label}</span>
                    <span
                      className={`text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider ${
                        dayData.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {dayData.active ? "Active OPD" : "Day Off"}
                    </span>
                  </div>

                  {dayData.active ? (
                    <div className="flex items-center gap-4 text-xs font-semibold text-gray-700">
                      {dayData.shift1Start && (
                        <span className="bg-primary-50 text-primary-800 px-3 py-1 rounded-xl border border-primary-100">
                          Shift 1: <strong>{dayData.shift1Start} - {dayData.shift1End}</strong>
                        </span>
                      )}
                      {dayData.shift2Start && (
                        <span className="bg-purple-50 text-purple-800 px-3 py-1 rounded-xl border border-purple-100">
                          Shift 2: <strong>{dayData.shift2Start} - {dayData.shift2End}</strong>
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">No scheduled clinic shifts</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CalendarTimelineView;
